import { apiErrorMessage } from '@agos/api-client'
import type { OrderListItem } from '@agos/api-client/model'
import {
  getGetOrdersQueryKey,
  getGetRestaurantTablesQueryKey,
  useDeleteRestaurantTablesTableId,
  useGetOrders,
  useGetRestaurantTables,
  useGetRestaurantZones,
  usePatchRestaurantTablesTableId,
  usePostRestaurantTables,
  usePutRestaurantTablesBulk,
  useGetSalesChannels,
} from '@agos/api-client/orders'
import {
  T,
  filterChannelsOf,
  mayHandOverTakings,
  orderableChannels,
  startOfDay,
  tt,
  useAuth,
} from '@agos/shared'
import {
  BUTTON_GHOST,
  BUTTON_PRIMARY,
  cn,
  ConfirmDialog,
  PageHeader,
  toast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  Calculator,
  Eye,
  GripVertical,
  Pencil,
  Plus,
  Receipt,
  RefreshCw,
  Save,
  ShoppingBag,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'wouter'

import { WaiterHandoverModal } from '../handover/WaiterHandoverModal'
import { useKitchenCalls } from '../kitchen-calls/use-kitchen-calls'
import { FloorCanvas } from './FloorCanvas'
import { OrderSidePanel } from './OrderSidePanel'
import { mayOpenOrderEditor } from '../order-rights'
import { TableInspector } from './TableInspector'
import { nextTableOrdinal, TABLE_NAME_PREFIX } from './table-names'
import {
  duplicateTableNames,
  ordersByTable,
  pickupOrders,
  statusForOrder,
} from './table-status'
import { tablesFrom, type RestaurantTable } from './types'
import { ZonesPanel } from './ZonesPanel'
import {
  channelForTable,
  findZone,
  tablesInZone,
  mayEditZones,
  zoneKey,
  zoneOptions,
  zonesFrom,
} from './zones'

/**
 * `/table-map` — *Mapovanie stolov*, ported from legacy `pages/table-map.tsx`.
 *
 * Two screens in one. In normal mode it is the room: a tile per table, ringed
 * by what the kitchen and the waiter have done, tapped to open the order or to
 * start one. In edit mode it is the furniture: tiles drag, and the positions go
 * back in one bulk write when edit mode closes.
 *
 * Ten-second poll on the orders, thirty on the layout — the room changes by the
 * minute, the furniture by the month (§14.1).
 */

const ORDERS_REFRESH_MS = 10_000
const TABLES_REFRESH_MS = 30_000
const ORDERS_PAGE_SIZE = '500'

const TILE_DEFAULTS = { capacity: 4, width: 90, height: 90, shape: 'rect' as const }

/** Who may close a shift from the floor plan. Ported from legacy. */

type PanelTarget = { kind: 'table'; tableId: number } | { kind: 'pickup'; orderId: number } | null

export function TableMapPage() {
  const queryClient = useQueryClient()
  const { user, hasPermission } = useAuth()
  const [, navigate] = useLocation()
  // §9.720, owner O95 - the kitchen's zvonček rings on the floor plan too.
  useKitchenCalls()

  const [editMode, setEditMode] = useState(false)
  /** §9.758 - *Zóny a pokladne* asks `restaurant_edit` (BACKEND.md §10A.413), the write key alone. */
  const mayZones = mayEditZones(hasPermission)
  // §9.337 - the chip's key rather than its name, because a zone may exist only as text on a table
  // and then has no id to hold on to. Empty is *every zone*, as it always was.
  const [activeZone, setActiveZone] = useState('')
  const [showZones, setShowZones] = useState(false)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [panel, setPanel] = useState<PanelTarget>(null)
  const [pendingDelete, setPendingDelete] = useState<RestaurantTable | null>(null)
  const [showHandover, setShowHandover] = useState(false)
  /** Positions being dragged, not yet written back. */
  const [draggedPositions, setDraggedPositions] = useState<
    Record<number, { x: number; y: number }>
  >({})
  const [now, setNow] = useState(() => Date.now())

  const { data: tablePayload, isLoading } = useGetRestaurantTables({
    query: { refetchInterval: TABLES_REFRESH_MS },
  })
  const tables = useMemo(() => tablesFrom(tablePayload), [tablePayload])

  // §9.337 - the real zones, polled with the furniture rather than with the orders: a zone changes
  // when somebody configures one, which is the same rhythm as moving a table.
  const { data: zonePayload } = useGetRestaurantZones({
    query: { refetchInterval: TABLES_REFRESH_MS },
  })

  const today = startOfDay(new Date()).toISOString()
  const { data: ordersResult, dataUpdatedAt } = useGetOrders(
    { date_from: today, page_size: ORDERS_PAGE_SIZE },
    { query: { refetchInterval: ORDERS_REFRESH_MS } },
  )
  const orders = useMemo(() => ordersResult?.data ?? [], [ordersResult])

  const createTable = usePostRestaurantTables()
  const updateTable = usePatchRestaurantTablesTableId()
  const deleteTable = useDeleteRestaurantTablesTableId()
  const moveTables = usePutRestaurantTablesBulk()

  /** Drives the "pred Xs" label without re-polling. */
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(tick)
  }, [])

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: getGetOrdersQueryKey() })
    void queryClient.invalidateQueries({ queryKey: getGetRestaurantTablesQueryKey() })
  }

  const invalidateTables = () =>
    void queryClient.invalidateQueries({ queryKey: getGetRestaurantTablesQueryKey() })

  // §9.393 - the counter queue is *on the premises and not at a table*, which is a question for
  // the channel table. Cached by react-query, so the room screen pays no extra request for it.
  const { data: salesChannels } = useGetSalesChannels()
  const channels = useMemo(() => filterChannelsOf(salesChannels), [salesChannels])
  /* §9.660, owner O43 - the channels a **new** order may go on, which is what the zone's select offers and what the
     tap resolves its stored slug against. A switched-off channel is not one of them, so a zone left pointing at one
     falls back rather than opening a channel no order can be placed on. */
  const orderable = useMemo(() => orderableChannels(channels), [channels])
  // §9.473 - the same list, so a table on any channel with `usesTables` is drawn rather than only the
  // literal `restaurant`. Moved below `channels` because it now reads it.
  const byTableName = useMemo(() => ordersByTable(orders, channels), [orders, channels])
  /* §9.663, FB-991 - how many open bills each name really has, for the tile to say so. The map above keeps the newer
     of two and a tile cannot be both, so this is a second question rather than a changed answer to the first. */
  const openBills = useMemo(() => duplicateTableNames(orders, channels), [orders, channels])
  const pickups = useMemo(() => pickupOrders(orders, channels), [orders, channels])

  /**
   * §9.337 - the real zones merged with whatever free text the tables still carry.
   *
   * This used to be `new Set(tables.map(table => table.zone))` alone, which is a column that is
   * **NULL on all 11 of the owner's rows** - the measurement that made §10A.117 build a real table.
   * Both are drawn because §10A.117 added `zone_id` *beside* `zone` rather than replacing it, so a
   * franchise that typed one keeps it.
   */
  const zones = useMemo(
    () => zoneOptions(zonesFrom(zonePayload), tables),
    [zonePayload, tables],
  )
  const selectedZone = findZone(zones, activeZone)
  const visibleTables = useMemo(() => tablesInZone(tables, selectedZone), [tables, selectedZone])
  const selected = tables.find((table) => table.id === selectedId) ?? null

  /**
   * Leaving edit mode writes every dragged position in one request. Legacy
   * fires this from an effect on `editMode` with the dependency list disabled;
   * here it is on the toggle itself, which is where the intent is.
   */
  const leaveEditMode = () => {
    const moved = Object.entries(draggedPositions).map(([id, position]) => ({
      id: Number(id),
      x: position.x,
      y: position.y,
    }))
    setEditMode(false)
    setSelectedId(null)
    if (moved.length === 0) return

    setDraggedPositions({})
    moveTables.mutate(
      { data: { updates: moved } },
      {
        onSuccess: invalidateTables,
        // §9.385 - `description: ''` four times on this screen. A collision or a zone that no
        // longer exists is named by the server; the screen said nothing at all.
        onError: (caught) =>
          toast({
            variant: 'destructive',
            title: tt('Uloženie polôh zlyhalo'),
            description: apiErrorMessage(caught, tt('Server zmenu odmietol.')),
          }),
      },
    )
  }

  const addTable = () => {
    // §9.223 — the first free number, not one past the count: after a deletion the count is short
    // and repeats a name, and `byTableName` joins orders on the name.
    const ordinal = nextTableOrdinal(tables)
    createTable.mutate(
      {
        data: {
          ...TILE_DEFAULTS,
          name: `${TABLE_NAME_PREFIX}${ordinal}`,
          x: 40 + ((ordinal * 30) % 600),
          y: 40 + Math.floor((ordinal * 30) / 600) * 110,
          // §9.337 - a table added while a zone is filtered joins that zone. By `zoneId` when the
          // zone is a real row, and by the legacy text when the chip came from a table's own
          // string, which is the only thing such a zone is.
          zone: selectedZone?.id === null ? selectedZone.name : null,
          zoneId: selectedZone?.id ?? null,
        },
      },
      {
        onSuccess: invalidateTables,
        onError: (caught) =>
          toast({
            variant: 'destructive',
            title: tt('Vytvorenie zlyhalo'),
            description: apiErrorMessage(caught, tt('Server stôl odmietol.')),
          }),
      },
    )
  }

  const tapTile = (table: RestaurantTable) => {
    if (editMode) {
      setSelectedId(table.id)
      return
    }
    if (byTableName.has(table.name)) {
      setPanel({ kind: 'table', tableId: table.id })
      return
    }
    // A free table goes straight to the order builder, pre-filled — which is what this comment always
    // said and what the navigation did not do. `/restaurant` is the *view* of occupied tables, so a
    // free one landed on a page with nothing on it for that table and no way to start: tapping a table
    // to seat somebody did nothing. `NewOrderPage` already reads both parameters (FB-192).
    /* §9.660, owner O43 - *"make sure i can also select which predajný kanál will be preselected when redirecting
       to Nová Objednávka"*. This was the literal `restaurant` on every tap, so a terrace table opened the restaurant
       channel whatever zone it was in. `channelForTable` resolves the zone's stored slug against the channels loaded
       above and falls back to this literal, which is what every table did before the field existed. */
    navigate(
      `/new-order?type=${channelForTable(table, zones, orderable, 'restaurant')}` +
        `&table=${encodeURIComponent(table.name)}`,
    )
  }

  const panelTable =
    panel?.kind === 'table' ? (tables.find((table) => table.id === panel.tableId) ?? null) : null
  const panelOrder: OrderListItem | null =
    panel?.kind === 'table'
      ? (byTableName.get(panelTable?.name ?? '') ?? null)
      : panel?.kind === 'pickup'
        ? (orders.find((order) => order.id === panel.orderId) ?? null)
        : null

  /**
   * **Pridať** opens the order; **Zaplatiť** and **Zrušiť** open the screen that settles it.
   *
   * `RestaurantPage` reads `pay` and `cancel` from the query and **never `edit`** — so *Pridať* used to
   * land on the list of occupied tables with the parameter ignored, which is FB-192's shape a second
   * time and the *"alebo aj upravovať?"* in the owner's note. `RestaurantPage.tsx:138` builds
   * `/new-order?type=restaurant&table=…&edit=…` for its own version of this button; that is the
   * destination, and it is used directly rather than routed through a page that would only forward it.
   * FB-194.
   */
  const goToRestaurant = (action: string) => {
    if (!panelOrder) return
    const tableQuery = panelTable ? `table=${encodeURIComponent(panelTable.name)}&` : ''
    if (action === 'edit') {
      /* §9.660 - the same resolution as the tap: an edit that reopened the builder on `restaurant` would move a
         terrace order's channel the moment somebody pressed *Pridať*. */
      const type = panelTable
        ? channelForTable(panelTable, zones, orderable, 'restaurant')
        : 'restaurant'
      navigate(`/new-order?type=${type}&${tableQuery}edit=${panelOrder.id}`)
      return
    }
    navigate(`/restaurant?${tableQuery}${action}=${panelOrder.id}&from=table-map`)
  }

  // react-query already knows when the list landed; keeping a second copy in
  // state only creates a way for the two to disagree.
  const secondsSinceRefresh = Math.max(0, Math.floor((now - (dataUpdatedAt || now)) / 1000))

  return (
    <div className="space-y-4">
      <PageHeader
        title={tt('Mapovanie stolov')}
        description={tt('Klikni na stôl pre detail aktívnej objednávky, alebo na voľný stôl pre novú.')}
        icon={<GripVertical className="w-5 h-5" />}
      />

      <div className="bg-card border border-border rounded-2xl p-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <ZoneButton active={activeZone === ''} onClick={() => setActiveZone('')}>
            <T>Všetky zóny</T>
          </ZoneButton>
          {zones.map((zone) => (
            <ZoneButton
              key={zoneKey(zone)}
              active={activeZone === zoneKey(zone)}
              onClick={() => setActiveZone(zoneKey(zone))}
            >
              {zone.name}
            </ZoneButton>
          ))}
        </div>

        <div className="flex-1" />

        {!editMode && (
          <div className="hidden md:flex items-center gap-3 text-[10px] text-muted-foreground">
            <LegendSwatch className="ring-amber-500/70" label={tt('V kuchyni')} />
            <LegendSwatch className="ring-blue-500/80" label={tt('Hotové')} />
            <LegendSwatch className="ring-emerald-500/60" label={tt('Servírovaný')} />
          </div>
        )}

        {!editMode && (
          <span
            className="hidden sm:inline-flex items-center gap-1 text-[10px] text-muted-foreground"
            title={tt('Stoly + objednávky sa obnovujú automaticky každých 10s.')}
            aria-live="polite"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Live · {tt('pred')} {secondsSinceRefresh}s
          </span>
        )}

        <button
          type="button"
          onClick={refresh}
          aria-label={tt('Obnoviť stav objednávok aj stolov')}
          title={tt('Obnoviť stav objednávok aj stolov')}
          className={cn(BUTTON_GHOST, 'p-2 rounded-lg')}
        >
          <RefreshCw className="w-4 h-4" />
        </button>

        {!editMode && mayHandOverTakings(user?.systemRole) && (
          <button
            type="button"
            onClick={() => setShowHandover(true)}
            title={tt('Odovzdať tržbu (uzávierka zmeny)')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25 text-xs font-bold transition-colors"
          >
            <Calculator className="w-3.5 h-3.5" /> <T>Odovzdať tržbu</T>
          </button>
        )}

        <button
          type="button"
          onClick={() => (editMode ? leaveEditMode() : setEditMode(true))}
          className={cn(
            'flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-colors',
            editMode
              ? 'bg-amber-500 text-amber-950 hover:bg-amber-400'
              : 'bg-muted hover:bg-muted/70 text-muted-foreground',
          )}
        >
          {editMode ? (
            <>
              <Save className="w-3.5 h-3.5" /> <T>Hotovo</T>
            </>
          ) : (
            <>
              <Pencil className="w-3.5 h-3.5" /> <T>Upraviť rozloženie</T>
            </>
          )}
        </button>

        {/* §9.337 - the button asks what writing a zone asks on the server: `restaurant_edit` since §9.758
          * (BACKEND.md §10A.413), the role before it. Offering a waiter a form the server refuses is FB-001's
          * failure, on a screen a waiter uses all day. */}
        {editMode && mayZones && (
          <button
            type="button"
            onClick={() => setShowZones((open) => !open)}
            data-zones-toggle
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-colors',
              showZones
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted hover:bg-muted/70 text-muted-foreground',
            )}
          >
            <Receipt className="w-3.5 h-3.5" /> <T>Zóny a pokladne</T>
          </button>
        )}

        {editMode && (
          <button
            type="button"
            onClick={addTable}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-500 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> <T>Pridať stôl</T>
          </button>
        )}
      </div>

      {editMode && showZones && mayZones && (
        <ZonesPanel zones={zones} channels={orderable} onClose={() => setShowZones(false)} />
      )}

      <div className="bg-card border border-border rounded-2xl p-3">
        {isLoading ? (
          <div className="h-[420px] flex items-center justify-center text-muted-foreground text-sm">
            <T>Načítavam…</T>
          </div>
        ) : tables.length === 0 ? (
          <div className="h-[420px] flex flex-col items-center justify-center text-muted-foreground text-sm gap-3">
            {/* FB-049: legacy prints a botched i18n migration here -
                „Upraviť rozloženiett(" → „Pridať stôl". Repaired. */}
            <p>
              <T>Žiadne stoly. Klikni „Upraviť rozloženie“ → „Pridať stôl“.</T>
            </p>
            <button
              type="button"
              onClick={() => {
                setEditMode(true)
                addTable()
              }}
              className={cn('flex items-center gap-1.5 px-3 py-2 rounded-lg', BUTTON_PRIMARY, 'text-xs')}
            >
              <Plus className="w-3.5 h-3.5" /> <T>Pridať prvý stôl</T>
            </button>
          </div>
        ) : (
          <FloorCanvas
            tables={visibleTables}
            editMode={editMode}
            selectedId={selectedId}
            onSelect={setSelectedId}
            positionOf={(table) => draggedPositions[table.id] ?? { x: table.x, y: table.y }}
            onDragTo={(tableId, position) =>
              setDraggedPositions((current) => ({ ...current, [tableId]: position }))
            }
            onTileTap={tapTile}
            ordersByTableName={byTableName}
            openBillsByTableName={openBills}
          />
        )}
      </div>

      {editMode && selected && (
        <TableInspector
          key={selected.id}
          table={selected}
          zones={zones}
          onSave={(changes) =>
            updateTable.mutate(
              { tableId: selected.id, data: changes },
              {
                onSuccess: invalidateTables,
                onError: (caught) =>
                  toast({
                    variant: 'destructive',
                    title: tt('Uloženie zlyhalo'),
                    description: apiErrorMessage(caught, tt('Server zmenu odmietol.')),
                  }),
              },
            )
          }
          onDelete={() => setPendingDelete(selected)}
          onClose={() => setSelectedId(null)}
        />
      )}

      {!editMode && visibleTables.length > 0 && (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Eye className="w-3.5 h-3.5" />{' '}
          <T>
            Voľné stoly otvárajú nový objednávkový formulár. Obsadené stoly otvoria panel s aktívnou
            objednávkou.
          </T>
        </p>
      )}

      {!editMode && (
        <PickupStrip
          orders={pickups}
          onOpen={(orderId) => setPanel({ kind: 'pickup', orderId })}
          onCreate={() => navigate('/new-order?type=bar')}
        />
      )}

      {pendingDelete && (
        <ConfirmDialog
          open
          title={tt('Zmazať stôl')}
          description={`${tt('Naozaj zmazať stôl')} „${pendingDelete.name}“?`}
          confirmLabel={tt('Zmazať')}
          variant="danger"
          onClose={() => setPendingDelete(null)}
          onConfirm={() => {
            const target = pendingDelete
            setPendingDelete(null)
            deleteTable.mutate(
              { tableId: target.id },
              {
                onSuccess: () => {
                  invalidateTables()
                  setSelectedId(null)
                },
                onError: (caught) =>
                  toast({
                    variant: 'destructive',
                    title: tt('Zmazanie zlyhalo'),
                    description: apiErrorMessage(caught, tt('Server zmazanie odmietol.')),
                  }),
              },
            )
          }}
        />
      )}

      {panel && (
        <OrderSidePanel
          title={panel.kind === 'table' ? `${tt('Stôl')} ${panelTable?.name ?? ''}` : tt('Osobný odber')}
          subtitle={panel.kind === 'pickup' ? (panelOrder?.customerName ?? undefined) : undefined}
          order={panelOrder}
          tableName={panelTable?.name ?? null}
          now={now}
          onClose={() => setPanel(null)}
          onItemsChanged={() =>
            void queryClient.invalidateQueries({ queryKey: getGetOrdersQueryKey() })
          }
          onGoToPay={() => goToRestaurant('pay')}
          onGoToAdd={() => goToRestaurant('edit')}
          onGoToCancel={() => goToRestaurant('cancel')}
          // §9.744, owner O114 - *Upraviť* on `edit_order` and `new_order` (BACKEND.md §10A.397, §9.744 amended), as on
          // Otvorené stoly.
          canEdit={mayOpenOrderEditor(hasPermission)}
        />
      )}

      {showHandover && user?.id != null && (
        <WaiterHandoverModal
          waiterId={user.id}
          waiterName={user.name ?? ''}
          onClose={() => setShowHandover(false)}
          onSubmitted={() =>
            toast({ title: tt('Tržba odovzdaná'), description: tt('Report bol uložený.') })
          }
        />
      )}
    </div>
  )
}

function ZoneButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-3 py-1.5 rounded-lg text-xs font-bold transition-colors',
        active
          ? 'bg-primary text-primary-foreground'
          : 'bg-muted hover:bg-muted/70 text-muted-foreground',
      )}
    >
      {children}
    </button>
  )
}

function LegendSwatch({ className, label }: { className: string; label: string }) {
  return (
    <span className="flex items-center gap-1">
      <span className={cn('w-3 h-3 rounded ring-2', className)} /> {label}
    </span>
  )
}

/**
 * The pickup strip, mirroring the bottom of the restaurant screen so a waiter
 * can see and act on counter orders without leaving the floor.
 */
function PickupStrip({
  orders,
  onOpen,
  onCreate,
}: {
  orders: OrderListItem[]
  onOpen: (orderId: number) => void
  onCreate: () => void
}) {
  return (
    <div className="bg-card border border-border rounded-2xl p-3 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
          <ShoppingBag className="w-3.5 h-3.5" /> <T>Osobný odber</T> ({orders.length})
        </p>
        <button
          type="button"
          onClick={onCreate}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-bold bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> <T>Nový odber</T>
        </button>
      </div>

      {orders.length === 0 ? (
        <p className="text-xs text-muted-foreground py-2">
          <T>Žiadne objednávky na odber</T>
        </p>
      ) : (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {orders.map((order) => {
            const isReady = statusForOrder(order) === 'ready'
            return (
              <button
                key={order.id}
                type="button"
                onClick={() => onOpen(order.id ?? 0)}
                className={cn(
                  'flex-shrink-0 min-w-[140px] text-left px-3 py-2 rounded-xl border transition-colors',
                  isReady
                    ? 'bg-blue-500/15 border-blue-500/40 animate-pulse'
                    : 'bg-muted/40 border-border hover:bg-muted/60',
                )}
              >
                <p className="font-mono text-xs font-bold text-primary">#{order.orderNumber}</p>
                <p className="text-sm font-semibold truncate">{order.customerName ?? '-'}</p>
                <p className="text-[10px] text-muted-foreground">
                  {order.items?.length ?? 0} {tt('pol.')}
                </p>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
